import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { apiUrl } from '../config/api';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import {
  formatMemberAddress,
  formatWhen,
  helpCategoryLabel,
  nextHelpAction,
} from '../constants/help';
import { apiFetch } from '../utils/session';
import './HelpRequest.css';

function AidRecord({ aid }) {
  if (!aid?.type) return null;
  const when = aid.date
    ? new Date(aid.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  return (
    <p className="help-meta">
      Aid: {aid.type}
      {aid.type === 'money' && aid.amount != null ? ` · ₹${aid.amount}` : ''}
      {when ? ` · ${when}` : ''}
      {aid.note ? ` · ${aid.note}` : ''}
    </p>
  );
}

function QueueCard({ item, role, busy, onStatus, onNote, onAid, onHelper }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(item.internalNote || '');
  const [aidType, setAidType] = useState(item.aid?.type || 'money');
  const [amount, setAmount] = useState(item.aid?.amount ?? '');
  const [aidDate, setAidDate] = useState(item.aid?.date ? String(item.aid.date).slice(0, 10) : '');
  const [aidNote, setAidNote] = useState(item.aid?.note || '');
  const [helperId, setHelperId] = useState('');
  const next = nextHelpAction(item.status);
  const address = formatMemberAddress(item.member);
  const away = item.homeArea !== item.helpArea;
  const showAdminResolve = role === 'Admin' && item.status !== 'Resolved' && item.status !== 'Closed' && next?.status !== 'Resolved';
  const summary = (item.description || '').trim();
  const summaryPreview = summary.length > 110 ? `${summary.slice(0, 110).trim()}…` : summary;

  return (
    <article className={`help-card help-queue-card${expanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="help-queue-summary"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
      >
        <div className="help-queue-summary-main">
          <span className="help-status" data-status={item.status}>
            {item.status}
          </span>
          <h2 className="help-card-title">
            {helpCategoryLabel(item.category)}
            {item.member?.name ? ` · ${item.member.name}` : ''}
          </h2>
          <p className="help-meta">
            Help in {item.helpArea}
            {away ? ` · Home area ${item.homeArea}` : ''}
            {item.createdAt ? ` · ${formatWhen(item.createdAt)}` : ''}
          </p>
          {!expanded && summaryPreview ? <p className="help-body help-queue-preview">{summaryPreview}</p> : null}
          {!expanded && !item.canAct ? (
            <p className="help-readonly help-queue-readonly">
              Read only. The {item.helpArea} Area Head handles this request.
            </p>
          ) : null}
        </div>
        <span className="help-queue-toggle" aria-hidden>
          {expanded ? <ChevronUp size={20} strokeWidth={2.25} /> : <ChevronDown size={20} strokeWidth={2.25} />}
        </span>
        <span className="help-queue-toggle-label">{expanded ? 'Hide details' : 'Show details'}</span>
      </button>

      {expanded ? (
        <div className="help-queue-details">
          {!item.canAct ? (
            <p className="help-readonly">
              Read only. The {item.helpArea} Area Head handles this request.
            </p>
          ) : null}
          {summary ? <p className="help-body">{summary}</p> : null}
          {item.followUp ? <p className="help-meta">Member follow-up: {item.followUp}</p> : null}
          <dl className="help-private">
            <dt>Phone</dt>
            <dd>{item.member?.phone || 'No phone on profile'}</dd>
            <dt>Address</dt>
            <dd>{address || 'No address on profile'}</dd>
            {item.member?.memberId ? (
              <>
                <dt>Member ID</dt>
                <dd>{item.member.memberId}</dd>
              </>
            ) : null}
          </dl>
          {item.canAct ? (
            <form
              className="help-field"
              onSubmit={(e) => {
                e.preventDefault();
                onNote(item._id, note);
              }}
            >
              <label htmlFor={`note-${item._id}`}>Internal note</label>
              <textarea
                id={`note-${item._id}`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={2000}
              />
              <button type="submit" className="help-btn" disabled={busy}>
                Save note
              </button>
            </form>
          ) : item.internalNote ? (
            <p className="help-meta">Internal note: {item.internalNote}</p>
          ) : null}
          <AidRecord aid={item.aid} />
          {item.helper?.name ? (
            <p className="help-meta">
              Helper: {item.helper.name}
              {item.helper.memberId ? ` · ${item.helper.memberId}` : ''}
            </p>
          ) : null}
          {item.canAct && item.status !== 'Closed' ? (
            <form
              className="help-field"
              onSubmit={(e) => {
                e.preventDefault();
                onHelper(item._id, helperId);
              }}
            >
              <label htmlFor={`helper-${item._id}`}>Helper member ID from {item.helpArea}</label>
              <input
                id={`helper-${item._id}`}
                value={helperId}
                onChange={(e) => setHelperId(e.target.value)}
                placeholder="SS-2026-00000"
              />
              <button type="submit" className="help-btn" disabled={busy}>
                Assign helper
              </button>
            </form>
          ) : null}
          {item.canAct && (item.status === 'Resolved' || item.status === 'Closed') ? (
            <form
              className="help-field"
              onSubmit={(e) => {
                e.preventDefault();
                onAid(item._id, {
                  type: aidType,
                  amount: aidType === 'money' ? amount : undefined,
                  date: aidDate,
                  note: aidNote,
                });
              }}
            >
              <label htmlFor={`aid-type-${item._id}`}>Aid given outside the app</label>
              <select id={`aid-type-${item._id}`} value={aidType} onChange={(e) => setAidType(e.target.value)}>
                <option value="money">Money</option>
                <option value="material">Material</option>
                <option value="introduction">Introduction</option>
                <option value="information">Information</option>
              </select>
              {aidType === 'money' ? (
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Amount"
                  required
                />
              ) : null}
              <label htmlFor={`aid-date-${item._id}`}>Date</label>
              <input
                id={`aid-date-${item._id}`}
                className="help-date-input"
                type="date"
                value={aidDate}
                onChange={(e) => setAidDate(e.target.value)}
                required
              />
              <textarea
                value={aidNote}
                onChange={(e) => setAidNote(e.target.value)}
                maxLength={500}
                required
                placeholder="Short note"
              />
              <button type="submit" className="help-btn" disabled={busy}>
                Save aid record
              </button>
            </form>
          ) : null}
          {item.statusHistory?.length ? (
            <ol className="help-history">
              {item.statusHistory.map((entry, index) => (
                <li key={`${entry.status}-${entry.at}-${index}`}>
                  {entry.status}
                  {entry.actorName ? ` · ${entry.actorName}` : ''}
                  {entry.at ? ` · ${formatWhen(entry.at)}` : ''}
                </li>
              ))}
            </ol>
          ) : null}
          {item.canAct ? (
            <div className="help-actions">
              {next ? (
                <button
                  type="button"
                  className="help-btn primary"
                  disabled={busy}
                  onClick={() => onStatus(item._id, next.status)}
                >
                  {next.label}
                </button>
              ) : null}
              {showAdminResolve ? (
                <button
                  type="button"
                  className="help-btn"
                  disabled={busy}
                  onClick={() => onStatus(item._id, 'Resolved')}
                >
                  Resolve
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default function HelpQueue() {
  const [requests, setRequests] = useState([]);
  const [scope, setScope] = useState('open');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const role = sessionStorage.getItem('sangramUserRole');

  const load = useCallback(async () => {
    setError('');
    const status = scope === 'open' ? '&status=open' : '';
    const res = await apiFetch(apiUrl(`/help/queue?${pagingParams(page, pageSize)}${status}`));
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not load the help queue');
    setRequests(data.requests || []);
    setTotal(data.total ?? (data.requests || []).length);
  }, [page, pageSize, scope]);

  useEffect(() => {
    let cancelled = false;
    const refresh = (showSpinner) => {
      if (showSpinner) setLoading(true);
      load()
        .catch((e) => {
          if (!cancelled) setError(e.message || 'Could not load the help queue');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    refresh(true);
    const iv = setInterval(() => refresh(false), 8000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const postAction = async (id, path, body, success) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl(`/help/${id}/${path}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not update request');
        return;
      }
      setNotice(success);
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const update = async (id, body, success) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl(`/help/${id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not update request');
        return;
      }
      setNotice(success);
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="help-page">
      <div className="help-shell">
        <header className="help-header">
          <div>
            <h1 className="help-title">Help queue</h1>
            <p className="help-sub">
              {role === 'Admin'
                ? 'Every area. Phone and address stay on this screen.'
                : 'Your zone, plus your members who asked for help in another area. You can update a request only when its help area is your zone.'}
            </p>
          </div>
        </header>

        <div className="help-filter" role="group" aria-label="Which requests to show">
          <button type="button" className="help-btn" aria-pressed={scope === 'open'} onClick={() => { setScope('open'); setPage(1); }}>
            Open
          </button>
          <button type="button" className="help-btn" aria-pressed={scope === 'all'} onClick={() => { setScope('all'); setPage(1); }}>
            All
          </button>
        </div>

        {error ? (
          <p className="help-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <div className="help-notice" role="status">
            <p className="help-notice-text">{notice}</p>
            <button
              type="button"
              className="help-notice-dismiss"
              aria-label="Dismiss"
              onClick={() => setNotice('')}
            >
              ×
            </button>
          </div>
        ) : null}
        {loading ? <p className="help-muted">Loading…</p> : null}
        {!loading && requests.length === 0 ? <p className="help-muted">No help requests in this list.</p> : null}

        <ul className="help-list">
          {requests.map((item) => (
            <li key={item._id}>
              <QueueCard
                item={item}
                role={role}
                busy={busy}
                onStatus={(id, status) => update(id, { status }, `Marked ${status}.`)}
                onNote={(id, internalNote) => update(id, { internalNote }, 'Note saved.')}
                onAid={(id, body) => postAction(id, 'aid', body, 'Aid record saved.')}
                onHelper={(id, memberId) => postAction(id, 'helper', { memberId }, 'Helper assigned.')}
              />
            </li>
          ))}
        </ul>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
